// ============================================================================
// SOURCES PAGE - Generic Document Analyzer
// ============================================================================

import { useState, useEffect } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { sourcesApi, type SourceWithStats, type ContentType } from '@/lib/api/client'
import { CONTENT_TYPES, CONTENT_TYPE_ICONS } from '@/lib/analyzer/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { SourceUploader } from './SourceUploader'
import { EntityReviewModal } from './EntityReviewModal'

interface SourcesPageProps {
  onSelectSource?: (sourceId: string) => void
}

export function SourcesPage({ onSelectSource }: SourcesPageProps) {
  const { accountId, projectId } = useSessionStore()
  const [sources, setSources] = useState<SourceWithStats[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUploader, setShowUploader] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [reviewingSourceId, setReviewingSourceId] = useState<string | null>(null)

  // Load sources
  useEffect(() => {
    if (!accountId || !projectId) {
      setLoading(false)
      setError('No account or project selected. Please select a project to continue.')
      return
    }

    const loadSources = async () => {
      try {
        setLoading(true)
        setError(null)
        const result = await sourcesApi.list(accountId, projectId)
        setSources(result)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load sources')
      } finally {
        setLoading(false)
      }
    }

    loadSources()
  }, [accountId, projectId])

  // Poll for sources with in-progress status
  useEffect(() => {
    if (!accountId || !projectId) return

    const sourcesInProgress = sources.filter(s => isStatusInProgress(s.status))
    if (sourcesInProgress.length === 0) return

    const pollInterval = setInterval(async () => {
      try {
        const result = await sourcesApi.list(accountId, projectId)
        setSources(result)
      } catch (err) {
        console.error('Failed to poll sources:', err)
      }
    }, 3000) // Poll every 3 seconds

    return () => clearInterval(pollInterval)
  }, [accountId, projectId, sources])

  // Handle new source created
  const handleSourceCreated = (source: SourceWithStats) => {
    setSources(prev => [source, ...prev])
    setShowUploader(false)
    // Optionally navigate to the source detail
    if (onSelectSource) {
      onSelectSource(source.id)
    }
  }

  // Handle source delete
  const handleDelete = async (sourceId: string) => {
    if (!accountId || !projectId) return
    if (!confirm('Are you sure you want to delete this source?')) return

    try {
      await sourcesApi.delete(accountId, projectId, sourceId)
      setSources(prev => prev.filter(s => s.id !== sourceId))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete source')
    }
  }

  // Handle entity review complete
  const handleEntityReviewComplete = async () => {
    setReviewingSourceId(null)
    // Refresh sources to get updated status
    if (accountId && projectId) {
      try {
        const result = await sourcesApi.list(accountId, projectId)
        setSources(result)
      } catch (err) {
        console.error('Failed to refresh sources:', err)
      }
    }
  }

  // Filter sources by search
  const filteredSources = sources.filter(source =>
    source.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    source.contentType.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Get content type info
  const getContentTypeInfo = (contentType: ContentType) => {
    return CONTENT_TYPES.find(ct => ct.id === contentType) || CONTENT_TYPES[CONTENT_TYPES.length - 1]
  }

  // Get status badge with icons
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'uploading':
        return (
          <Badge variant="secondary" className="gap-1">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Uploading
          </Badge>
        )
      case 'parsing':
        return (
          <Badge variant="secondary" className="gap-1">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Parsing
          </Badge>
        )
      case 'analyzing':
        return (
          <Badge variant="secondary" className="gap-1">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Analyzing
          </Badge>
        )
      case 'detecting-entities':
        return (
          <Badge variant="secondary" className="gap-1">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
            Detecting Entities
          </Badge>
        )
      case 'ready-for-review':
        return <Badge variant="default" className="bg-amber-500 hover:bg-amber-600">Ready for Review</Badge>
      case 'completed':
        return <Badge variant="default" className="bg-green-600 hover:bg-green-700">Completed</Badge>
      case 'error':
        return <Badge variant="destructive">Error</Badge>
      case 'pending':
        return <Badge variant="outline">Pending</Badge>
      case 'analyzed':
        return <Badge variant="default">Analyzed</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  // Check if status is in progress
  const isStatusInProgress = (status: string) => {
    return ['uploading', 'parsing', 'analyzing', 'detecting-entities'].includes(status)
  }

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return (
      <div className="h-full overflow-y-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-zinc-200 dark:bg-zinc-700 rounded" />
          <div className="h-32 bg-zinc-200 dark:bg-zinc-700 rounded" />
          <div className="h-32 bg-zinc-200 dark:bg-zinc-700 rounded" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full overflow-y-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Setup Required</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
              To use the Sources feature, you need to have an account and project selected.
            </p>
            <Button onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Sources</h1>
          <p className="text-zinc-500 dark:text-zinc-400">
            Upload documents to analyze and extract insights
          </p>
        </div>
        <Button onClick={() => setShowUploader(true)}>
          + New Source
        </Button>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <Input
          placeholder="Search sources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Upload Dialog */}
      {showUploader && (
        <SourceUploader
          onClose={() => setShowUploader(false)}
          onSourceCreated={handleSourceCreated}
        />
      )}

      {/* Sources List */}
      {filteredSources.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-zinc-500 dark:text-zinc-400 mb-4">
              {sources.length === 0
                ? 'No sources yet. Upload a document to get started.'
                : 'No sources match your search.'}
            </p>
            {sources.length === 0 && (
              <Button onClick={() => setShowUploader(true)}>
                Upload First Source
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredSources.map(source => {
            const contentTypeInfo = getContentTypeInfo(source.contentType)
            return (
              <Card
                key={source.id}
                className="cursor-pointer hover:border-zinc-400 dark:hover:border-zinc-600 transition-colors"
                onClick={() => onSelectSource?.(source.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const Icon = CONTENT_TYPE_ICONS[contentTypeInfo.icon]
                        return Icon ? <Icon className="h-6 w-6 text-zinc-500" /> : null
                      })()}
                      <div>
                        <CardTitle className="text-lg">{source.title}</CardTitle>
                        <CardDescription>
                          {contentTypeInfo.name} &middot; {formatDate(source.createdAt)}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(source.status)}
                      {source.status === 'ready-for-review' && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            setReviewingSourceId(source.id)
                          }}
                        >
                          Review Entities
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(source.id)
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-zinc-500 dark:text-zinc-400">
                    {source.facetStats.total > 0 ? (
                      <>
                        <span>{source.facetStats.total} facets found</span>
                        {source.facetStats.pending > 0 && (
                          <Badge variant="outline" className="bg-amber-50 dark:bg-amber-900/20">
                            {source.facetStats.pending} pending review
                          </Badge>
                        )}
                        {source.facetStats.accepted > 0 && (
                          <Badge variant="outline" className="bg-green-50 dark:bg-green-900/20">
                            {source.facetStats.accepted} accepted
                          </Badge>
                        )}
                        {source.facetStats.mapped > 0 && (
                          <Badge variant="outline" className="bg-blue-50 dark:bg-blue-900/20">
                            {source.facetStats.mapped} created
                          </Badge>
                        )}
                      </>
                    ) : source.status === 'pending' ? (
                      <span>Not analyzed yet</span>
                    ) : source.status === 'analyzing' ? (
                      <span>Analysis in progress...</span>
                    ) : source.status === 'error' ? (
                      <span className="text-red-500">{source.analysisError || 'Analysis failed'}</span>
                    ) : (
                      <span>No facets detected</span>
                    )}
                  </div>
                  {source.tags && source.tags.length > 0 && (
                    <div className="mt-2 flex gap-1">
                      {source.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Entity Review Modal */}
      {reviewingSourceId && (
        <EntityReviewModal
          sourceId={reviewingSourceId}
          onClose={() => setReviewingSourceId(null)}
          onComplete={handleEntityReviewComplete}
        />
      )}
    </div>
  )
}
